/**
 * Catalogue des fournisseurs IMAP / SMTP supportés au lancement.
 *
 * Couvre ~90 % du marché français : Gmail / Workspace, Outlook / Office 365,
 * iCloud, OVH (mail pro et mail mutualisé), Free.
 *
 * Chaque preset porte :
 *  - les paramètres techniques (host, port, TLS) pré-remplis quand on
 *    détecte le domaine de l'adresse e-mail saisie ;
 *  - un bloc d'aide spécifique (mot de passe d'application, activation
 *    IMAP côté webmail, etc.) avec lien vers la doc officielle ;
 *  - les domaines associés pour la détection automatique.
 */

export interface MailProviderHelpStep {
  title: string;
  description: string;
}

export interface MailProvider {
  id: string;
  label: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  /** Domaines détectés automatiquement à partir de l'adresse e-mail. */
  domains: string[];
  /** Phrase courte affichée dans le sélecteur de preset. */
  hint?: string;
  /** Bloc d'aide étape par étape rendu dès que le provider est détecté. */
  help?: {
    headline: string;
    intro?: string;
    steps: MailProviderHelpStep[];
    docUrl?: string;
    docLabel?: string;
  };
}

export const MAIL_PROVIDERS: MailProvider[] = [
  {
    id: "gmail",
    label: "Gmail / Google Workspace",
    imapHost: "imap.gmail.com",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.gmail.com",
    smtpPort: 465,
    smtpSecure: true,
    domains: ["gmail.com", "googlemail.com"],
    hint: "Activez la 2FA puis créez un mot de passe d'application.",
    help: {
      headline: "Connecter un compte Gmail ou Google Workspace",
      intro:
        "Google n'autorise plus la connexion IMAP avec votre mot de passe " +
        "habituel. Vous devez créer un mot de passe d'application dédié.",
      steps: [
        {
          title: "Activez la validation en deux étapes",
          description:
            "Sur myaccount.google.com → Sécurité → Validation en deux étapes. " +
            "C'est obligatoire pour pouvoir générer un mot de passe d'application.",
        },
        {
          title: "Créez un mot de passe d'application",
          description:
            "Sur myaccount.google.com/apppasswords. Choisissez « Autre (nom personnalisé) », " +
            "saisissez « OneNext », puis copiez le mot de passe à 16 caractères.",
        },
        {
          title: "Collez-le ci-dessous",
          description:
            "Utilisez ce mot de passe à 16 caractères dans le champ « Mot de passe » " +
            "(et non votre mot de passe Google habituel).",
        },
      ],
      docUrl: "https://myaccount.google.com/apppasswords",
      docLabel: "Générer mon mot de passe Gmail →",
    },
  },
  {
    id: "outlook",
    label: "Outlook / Office 365 / Hotmail",
    imapHost: "outlook.office365.com",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.office365.com",
    smtpPort: 587,
    // Office 365 utilise STARTTLS sur le port 587, pas du SSL implicite.
    smtpSecure: false,
    domains: [
      "outlook.com",
      "outlook.fr",
      "hotmail.com",
      "hotmail.fr",
      "live.com",
      "live.fr",
      "msn.com",
    ],
    hint: "Si la 2FA est active, créez un mot de passe d'application.",
    help: {
      headline: "Connecter un compte Outlook / Office 365",
      intro:
        "Microsoft impose souvent un mot de passe d'application quand la " +
        "double authentification est activée sur le compte.",
      steps: [
        {
          title: "Vérifiez que IMAP est activé",
          description:
            "Sur outlook.live.com → Paramètres → Courrier → Synchroniser le courrier, " +
            "vérifiez que « Laisser les appareils et applications utiliser POP / IMAP » est activé.",
        },
        {
          title: "Créez un mot de passe d'application si la 2FA est active",
          description:
            "Sur account.microsoft.com/security → Options de sécurité avancées → " +
            "Mots de passe d'application. Copiez le mot de passe généré.",
        },
        {
          title: "Collez-le dans le champ « Mot de passe »",
          description:
            "Si vous n'avez pas la 2FA, utilisez votre mot de passe Microsoft habituel.",
        },
      ],
      docUrl: "https://account.microsoft.com/security",
      docLabel: "Créer mon mot de passe Outlook →",
    },
  },
  {
    id: "icloud",
    label: "iCloud Mail",
    imapHost: "imap.mail.me.com",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.mail.me.com",
    smtpPort: 587,
    smtpSecure: false,
    domains: ["icloud.com", "me.com", "mac.com"],
    hint: "Apple exige systématiquement un mot de passe d'application.",
    help: {
      headline: "Connecter un compte iCloud",
      intro:
        "Apple n'autorise jamais l'accès IMAP avec votre mot de passe Apple ID. " +
        "Vous devez générer un mot de passe spécifique à l'application.",
      steps: [
        {
          title: "Activez l'authentification à deux facteurs",
          description:
            "Sur appleid.apple.com → Connexion et sécurité. Sans la 2FA, " +
            "Apple ne propose pas la création de mot de passe d'application.",
        },
        {
          title: "Générez un mot de passe pour OneNext",
          description:
            "Sur appleid.apple.com → Connexion et sécurité → Mots de passe " +
            "spécifiques à une app → Générer. Nommez-le « OneNext ».",
        },
        {
          title: "Collez les 19 caractères ci-dessous",
          description:
            "Le mot de passe ressemble à xxxx-xxxx-xxxx-xxxx. Conservez les " +
            "tirets en collant la valeur.",
        },
      ],
      docUrl: "https://appleid.apple.com/account/manage",
      docLabel: "Générer mon mot de passe iCloud →",
    },
  },
  {
    id: "ovh",
    label: "OVH (Mail Pro & mutualisé)",
    imapHost: "ssl0.ovh.net",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "ssl0.ovh.net",
    smtpPort: 465,
    smtpSecure: true,
    // Détection : OVH héberge le mail pour beaucoup de domaines personnalisés.
    // On déclenche le preset uniquement pour les domaines OVH génériques —
    // pour un domaine pro, l'utilisateur sélectionne manuellement le preset.
    domains: ["ovh.fr", "ovh.net", "ovh.com", "ovhcloud.com"],
    hint: "Identifiant = adresse e-mail complète, port IMAP 993 (SSL).",
    help: {
      headline: "Connecter un compte OVH",
      intro:
        "OVH utilise un seul serveur (ssl0.ovh.net) pour tous les comptes " +
        "mail pro et mutualisés. L'identifiant est toujours l'adresse e-mail complète.",
      steps: [
        {
          title: "Vérifiez que IMAP est activé",
          description:
            "Connectez-vous à mail.ovh.net puis Paramètres → Compte → IMAP. " +
            "Activez la case « Activer l'accès IMAP » si elle ne l'est pas.",
        },
        {
          title: "Utilisez votre mot de passe e-mail",
          description:
            "OVH n'utilise pas de mot de passe d'application : c'est le mot de " +
            "passe défini dans votre Espace Client OVH pour ce compte mail.",
        },
        {
          title: "Identifiant = adresse e-mail complète",
          description:
            "Le champ « Identifiant » doit contenir l'adresse complète " +
            "(ex. moi@mondomaine.fr), pas seulement la partie avant le @.",
        },
      ],
      docUrl:
        "https://help.ovhcloud.com/csm/fr-mail-configuration-imap-smtp",
      docLabel: "Documentation OVH : configuration IMAP / SMTP",
    },
  },
  {
    id: "free",
    label: "Free Mail",
    imapHost: "imap.free.fr",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.free.fr",
    smtpPort: 465,
    smtpSecure: true,
    domains: ["free.fr", "aliceadsl.fr"],
    hint: "Activez l'accès IMAP depuis votre interface Free.",
    help: {
      headline: "Connecter un compte Free",
      intro:
        "Free désactive l'accès IMAP par défaut. Il faut l'activer manuellement " +
        "depuis votre interface abonné.",
      steps: [
        {
          title: "Activez l'accès IMAP",
          description:
            "Sur subscribe.free.fr → Gérer mon compte e-mail → cochez " +
            "« Autoriser l'accès IMAP » puis enregistrez.",
        },
        {
          title: "Utilisez votre mot de passe Free",
          description:
            "Free n'utilise pas de mot de passe d'application : c'est votre " +
            "mot de passe principal de l'espace abonné.",
        },
        {
          title: "Patientez quelques minutes",
          description:
            "L'activation IMAP côté Free peut prendre 5 à 10 minutes avant " +
            "que la connexion ne fonctionne.",
        },
      ],
      docUrl: "https://assistance.free.fr/articles/configurer-un-logiciel-de-messagerie-imap-152",
      docLabel: "Assistance Free : configurer un logiciel IMAP",
    },
  },
  {
    id: "yahoo",
    label: "Yahoo Mail",
    imapHost: "imap.mail.yahoo.com",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.mail.yahoo.com",
    smtpPort: 465,
    smtpSecure: true,
    domains: ["yahoo.fr", "yahoo.com", "yahoo.co.uk", "ymail.com"],
    hint: "Yahoo exige un mot de passe d'application depuis 2021.",
    help: {
      headline: "Connecter un compte Yahoo Mail",
      intro:
        "Yahoo n'accepte plus votre mot de passe habituel pour les connexions " +
        "IMAP. Vous devez générer un mot de passe d'application dédié.",
      steps: [
        {
          title: "Accédez aux paramètres de sécurité Yahoo",
          description:
            "Connectez-vous sur login.yahoo.com, puis allez dans " +
            "Sécurité du compte → Mot de passe de l'app.",
        },
        {
          title: "Générez un mot de passe pour OneNext",
          description:
            "Cliquez sur « Générer un mot de passe d'application », " +
            "nommez-le « OneNext » et copiez le mot de passe affiché.",
        },
        {
          title: "Collez-le dans le champ Mot de passe",
          description:
            "Utilisez ce mot de passe généré (pas votre mot de passe Yahoo habituel).",
        },
      ],
      docUrl: "https://login.yahoo.com/account/security",
      docLabel: "Gérer la sécurité Yahoo →",
    },
  },
  {
    id: "laposte",
    label: "La Poste Mail",
    imapHost: "imap.laposte.net",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.laposte.net",
    smtpPort: 465,
    smtpSecure: true,
    domains: ["laposte.net"],
    hint: "Identifiant = adresse e-mail complète, port IMAP 993 (SSL).",
    help: {
      headline: "Connecter un compte La Poste",
      intro:
        "La Poste Mail utilise votre mot de passe habituel pour IMAP. " +
        "Vérifiez que l'accès IMAP est bien activé sur votre compte.",
      steps: [
        {
          title: "Vérifiez que le compte est actif",
          description:
            "Connectez-vous sur laposte.net/courrier et vérifiez que " +
            "votre compte est bien accessible.",
        },
        {
          title: "Utilisez votre mot de passe La Poste",
          description:
            "Le mot de passe IMAP est le même que celui de votre " +
            "espace laposte.net (pas de mot de passe d'application).",
        },
        {
          title: "Identifiant = adresse e-mail complète",
          description:
            "Saisissez votre adresse complète (ex. prenom.nom@laposte.net) " +
            "dans le champ identifiant.",
        },
      ],
      docUrl: "https://www.laposte.net/aide/courrier",
      docLabel: "Aide La Poste →",
    },
  },
  {
    id: "orange",
    label: "Orange / Wanadoo",
    imapHost: "imap.orange.fr",
    imapPort: 993,
    imapSecure: true,
    smtpHost: "smtp.orange.fr",
    smtpPort: 465,
    smtpSecure: true,
    domains: ["orange.fr", "wanadoo.fr", "club-internet.fr"],
    hint: "Utilisez votre mot de passe Orange habituel.",
    help: {
      headline: "Connecter un compte Orange / Wanadoo",
      intro:
        "Orange utilise votre mot de passe habituel pour l'accès IMAP. " +
        "Assurez-vous que l'accès messagerie externe est activé.",
      steps: [
        {
          title: "Vérifiez l'accès messagerie",
          description:
            "Sur mail.orange.fr → Paramètres → Mon compte, vérifiez " +
            "que « Accès depuis un logiciel de messagerie » est activé.",
        },
        {
          title: "Utilisez votre mot de passe Orange",
          description:
            "Le mot de passe est celui de votre compte orange.fr. " +
            "Orange n'utilise pas de mot de passe d'application.",
        },
        {
          title: "Identifiant = adresse e-mail complète",
          description:
            "Saisissez l'adresse complète (ex. prenom@orange.fr) " +
            "comme identifiant IMAP.",
        },
      ],
      docUrl: "https://assistance.orange.fr/computers-peripheriques/installer-et-utiliser/messaging/",
      docLabel: "Assistance Orange →",
    },
  },
];

export const CUSTOM_PROVIDER_ID = "custom";

/**
 * Détecte le provider à partir de l'adresse e-mail saisie.
 * Renvoie `null` si aucun domaine connu ne correspond — l'utilisateur
 * doit alors saisir manuellement les paramètres.
 */
export function detectProvider(email: string): MailProvider | null {
  const at = email.lastIndexOf("@");
  if (at < 0) return null;
  const domain = email.slice(at + 1).trim().toLowerCase();
  if (!domain) return null;
  for (const p of MAIL_PROVIDERS) {
    if (p.domains.includes(domain)) return p;
  }
  return null;
}

export function getProviderById(id: string): MailProvider | null {
  return MAIL_PROVIDERS.find((p) => p.id === id) ?? null;
}
